'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { SpearheadProgress, SpearheadDomain } from '@/lib/types/spearhead';
import { loadSpearheadProgress, isSpearheadActive, getSpearheadDaysRemaining, getDomainColor } from '@/lib/spearheadEngine';
import { Crosshair, ChevronRight, Shield } from 'lucide-react';

interface SpearheadDashboardCardProps {
  userId: string;
}

export default function SpearheadDashboardCard({ userId }: SpearheadDashboardCardProps) {
  const [progress, setProgress] = useState<SpearheadProgress | null>(null);

  useEffect(() => {
    if (!isSpearheadActive()) return;
    loadSpearheadProgress(userId).then(setProgress).catch(() => {});
  }, [userId]);

  if (!isSpearheadActive() || !progress) return null;

  const daysRemaining = getSpearheadDaysRemaining();
  const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
  const avgControl = domains.reduce((s, d) => s + progress.sectors[d].controlPct, 0) / 4;
  const isComplete = !!progress.victoryAchievedAt;

  // Determine severity from avg control (drives bar / accent color)
  const severity: 'gold' | 'emerald' | 'red' = isComplete
    ? 'emerald'
    : avgControl < 35
      ? 'red'
      : 'gold';

  const severityTokens = {
    gold:    { bar: 'bg-amber-500',    border: 'border-amber-500/30',    brkt: 'border-amber-500/60',    badge: 'text-amber-400',    accent: 'via-amber-500/70',    glow: 'shadow-[0_0_40px_-16px_rgba(245,158,11,0.25)]' },
    emerald: { bar: 'bg-emerald-500',  border: 'border-emerald-500/35',  brkt: 'border-emerald-500/60', badge: 'text-emerald-400',  accent: 'via-emerald-500/70', glow: 'shadow-[0_0_40px_-16px_rgba(74,222,128,0.25)]' },
    red:     { bar: 'bg-red-500',      border: 'border-red-500/35',      brkt: 'border-red-500/60',     badge: 'text-red-400',      accent: 'via-red-500/70',     glow: 'shadow-[0_0_40px_-16px_rgba(239,68,68,0.25)]' },
  }[severity];

  const domainLabels: Record<SpearheadDomain, string> = {
    alpha: 'STR',
    bravo: 'END',
    charlie: 'BW',
    delta: 'LOAD',
  };

  const secured = domains.filter(d => progress.sectors[d].controlPct >= 80).length;

  return (
    <Link href="/operations" className="block group active:scale-[0.99] transition">
      <section
        className={`relative overflow-hidden rounded-xl border bg-hud-ink/85 ${severityTokens.border} ${severityTokens.glow} transition hover:border-amber-400/50`}
      >
        {/* Severity side bar */}
        <span aria-hidden className={`absolute top-0 bottom-0 left-0 w-1 ${severityTokens.bar}`} />

        {/* Top accent line */}
        <div
          aria-hidden
          className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${severityTokens.accent} to-transparent`}
        />

        {/* Corner brackets (bottom 2 — standard variant) */}
        <span aria-hidden className={`pointer-events-none absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l ${severityTokens.brkt}`} />
        <span aria-hidden className={`pointer-events-none absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r ${severityTokens.brkt}`} />

        {/* Faint scan grid texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.6) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative px-4 pt-2.5 pb-3 pl-4">
          {/* Badge header row */}
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-0.5 h-3 ${severityTokens.bar}`} aria-hidden />
              <Crosshair className={`w-3.5 h-3.5 ${severityTokens.badge}`} />
              <span className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold truncate ${severityTokens.badge}`}>
                Spearhead
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 hidden sm:inline truncate">
                SYS·SPR·01
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isComplete ? (
                <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">
                  <Shield className="w-3 h-3" /> Secured
                </span>
              ) : (
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 tabular-nums">
                  {daysRemaining}d
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-0.5 transition" />
            </div>
          </div>

          {/* Big territory readout — feels weighty */}
          <div className="flex items-end justify-between gap-3 mb-2.5">
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-0.5">
                Territory Held
              </div>
              <div className="flex items-baseline gap-1.5">
                <span
                  className="font-mono text-[34px] leading-none font-black tabular-nums"
                  style={{ color: getDomainColor(avgControl), textShadow: `0 0 14px ${getDomainColor(avgControl)}40` }}
                >
                  {Math.round(avgControl)}
                </span>
                <span className="font-mono text-[16px] font-bold text-slate-600">%</span>
              </div>
            </div>

            {/* Sectors-secured chip */}
            <div className="text-right">
              <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500 mb-0.5">
                Sectors
              </div>
              <div className="font-mono text-base font-bold text-slate-200 tabular-nums">
                {secured}<span className="text-slate-600">/4</span>
              </div>
            </div>
          </div>

          {/* Mini sector segmented gauge — 4 vertical bars */}
          <div className="grid grid-cols-4 gap-1.5">
            {domains.map(d => {
              const pct = Math.round(progress.sectors[d].controlPct);
              const c = getDomainColor(pct);
              return (
                <div key={d} className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">{domainLabels[d]}</span>
                    <span className="font-mono text-[9px] font-bold tabular-nums" style={{ color: c }}>{pct}</span>
                  </div>
                  <div className="relative h-1 bg-slate-900 rounded-sm overflow-hidden border border-slate-800">
                    {/* tick marks at 25/50/75 */}
                    <span aria-hidden className="absolute top-0 bottom-0 left-1/4 w-px bg-slate-700/50" />
                    <span aria-hidden className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-700/50" />
                    <span aria-hidden className="absolute top-0 bottom-0 left-3/4 w-px bg-slate-700/50" />
                    <div
                      className="absolute inset-y-0 left-0 transition-all duration-700"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${c}aa, ${c})`,
                        boxShadow: `0 0 6px ${c}80`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer — consistency telemetry */}
          <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.18em]">
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Consistency</span>
              <span className="text-slate-200 font-bold tabular-nums">
                {(progress.consistencyIndex * 100).toFixed(0)}<span className="text-slate-600">%</span>
              </span>
            </div>
            <span className={`${severityTokens.badge} font-bold tracking-[0.22em]`}>
              Open &rarr;
            </span>
          </div>
        </div>
      </section>
    </Link>
  );
}
